import useGetMyUncheckedAiAnswer from '@/hooks/qna/mobile/home/useGetMyUncheckedAiAnswer';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import useNavigate from '@/hooks/useNavigate';
import { tokenState } from '@/recoil/atoms/common/auth';
import { EAirbridgeLinkType } from '@/shared/types';
import { AiAnswerStatus, QuestionStatus } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { isWebView } from '@/utils/mobile/RNPlugins';
import { useRecoilValue } from 'recoil';

export type TrackingInfo = {
  buttonText?: string;
  qnaId?: string;
  qnaTitle?: string;
  qnaCategory?: string;
  qnaSubCategory?: string;
  linkType?: EAirbridgeLinkType;
};

const useCanProgressQnaWrite = () => {
  const token = useRecoilValue(tokenState);
  const { moveToPage } = useCustomRouter();
  const { setShowAuthModal, setTrackInfo } = useAuthModal();
  const remainAiAnswer = useGetMyUncheckedAiAnswer();
  const { showModal, closeModal } = useModal();
  const navigate = useNavigate();

  const onClickQuestionDetail = (questionPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AI.appScreenName,
      params: {
        questionPid,
      },
    });

    closeModal();
  };

  const canProgressQnaWrite = (aiAnswerStatus: AiAnswerStatus, status: QuestionStatus) => {
    if (
      (aiAnswerStatus === AiAnswerStatus.GenerationComplete ||
        aiAnswerStatus === AiAnswerStatus.Generating ||
        aiAnswerStatus === AiAnswerStatus.GenerationRequested ||
        aiAnswerStatus === AiAnswerStatus.GenerationFailed) &&
      status !== QuestionStatus.PaymentFailed
    ) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '기존 답변을 먼저 확인해 주세요',
          message: '고민이 해결됐는지 알려주신 뒤 다음 질문을 작성할 수 있어요.',
          buttons: [
            {
              label: '닫기',
              onClick: () => closeModal(),
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '답변 보기',
              onClick: () => onClickQuestionDetail(remainAiAnswer?.questionPid ?? ''),
            },
          ],
        },
      });

      return false;
    }

    return true;
  };

  const clickProgressWrite = async (trackInfo?: {
    content: string;
    buttonText: string;
  }) => {
    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
      setTrackInfo({
        campaign: 'web_content_qna',
        content: trackInfo?.content ?? '',
        buttonText: trackInfo?.buttonText ?? '',
      });
    } else {
      // 로그인 정보가 있는 경우
      if (isWebView()) {
        if (remainAiAnswer && !canProgressQnaWrite(remainAiAnswer.aiAnswerStatus, remainAiAnswer.status)) {
          return;
        }
      }
    }
    navigate.navigateToQnaWrite();
  };

  return {
    clickProgressWrite,
  };
};

export default useCanProgressQnaWrite;
